import { ButtonComponent } from '@/components/commons'
import { EditIconPen } from '@/public/assets/v2/images/icons'
import { cssClass } from '@/shared/utils'
import { AcceptDeclineButtons } from 'containers/profile/components/buttons/AcceptDeclineButtons'
import { BlockRemoveConnection } from 'containers/profile/components/buttons/BlockRemoveButton'
import { MessageButton } from 'containers/profile/components/buttons/MessageButton'
import { RequestConnectionButton } from 'containers/profile/components/buttons/RequestConnectionButton'
import {
  useIsOwnProfile,
  useProfile,
  useProfileRelationType,
} from 'containers/profile/hooks/useProfile'
import { ProfileRelationType } from 'containers/profile/types/profile'
import { User } from 'containers/user/types/user'
import { BrowserContext } from 'context'
import { useContext, useState } from 'react'
import { APP_LINKS, joinWithSlash } from 'utilities/links'
import { ViewProfileButton } from './ViewProfileButton'

interface ActionButtonsProps {
  profile?: User
  useDismiss?: boolean
  useDismissForConnections?: boolean
  isInConnectionCard?: boolean
  useViewProfile?: boolean
  useBlockOrRemove?: boolean
  customDismiss?: () => void
  loadingButtons?: boolean
}

export const ActionButtons = ({
  profile,
  useDismiss,
  isInConnectionCard = false,
  useViewProfile = true,
  useBlockOrRemove = true,
  useDismissForConnections = false,
  loadingButtons,
  customDismiss,
}: ActionButtonsProps) => {
  const { isMobile } = useContext(BrowserContext)
  const { profile: currentProfile, isLoading } = useProfile()
  const profileUsing = profile || currentProfile
  const ownProfile = useIsOwnProfile(profileUsing)
  const relation = useProfileRelationType(profileUsing)
  const [removingOrBlocking, setRemovingOrBlocking] = useState(false)

  if (isLoading) return null

  if (relation === ProfileRelationType.CONNECTION_RECEIVED) {
    return (
      <div className="flex gap-12">
        <AcceptDeclineButtons profile={profileUsing} />
      </div>
    )
  }

  if (
    relation === ProfileRelationType.CONNECTION ||
    relation === ProfileRelationType.BLOCKED
  ) {
    return (
      <div
        className={cssClass([
          'flex gap-12 items-center justify-flex-end',
          !isInConnectionCard && 'pr-12',
        ])}
      >
        {relation === ProfileRelationType.CONNECTION ? (
          <MessageButton
            profile={profileUsing}
            loading={removingOrBlocking}
            buttonClassName={isMobile ? 'full-w' : ''}
          />
        ) : (
          <ButtonComponent
            size="xSmall"
            variation={
              profile && profile?.id !== currentProfile?.id
                ? 'primary'
                : 'secondary'
            }
            className="uppercase"
            label="Blocked"
            customSize={{ padding: 35 }}
            disabled={
              removingOrBlocking || relation === ProfileRelationType.BLOCKED
            }
            loading={removingOrBlocking}
          />
        )}

        {profile && profile?.id !== currentProfile?.id && useViewProfile && (
          <ViewProfileButton
            disabled={
              removingOrBlocking || relation === ProfileRelationType.BLOCKED
            }
            loading={removingOrBlocking}
            profile={profile}
          />
        )}

        {useDismissForConnections && (
          <ButtonComponent
            size="xSmall"
            variation="secondary"
            className={cssClass(['uppercase'])}
            label="Dismiss"
            fullWidth
            disabled={loadingButtons}
            loading={loadingButtons}
            onClick={customDismiss}
          />
        )}

        {!!useBlockOrRemove && (
          <BlockRemoveConnection
            setRemovingOrBlockingConnection={setRemovingOrBlocking}
            profile={profileUsing}
          />
        )}
      </div>
    )
  }

  return (
    <div
      className={cssClass([
        'flex gap-12 items-center justify-flex-end',
        !isInConnectionCard && 'pr-12',
      ])}
    >
      {ownProfile && !isInConnectionCard && (
        <ButtonComponent
          size="xSmall"
          variation="secondary"
          className="w-100 uppercase"
          to={joinWithSlash(
            profileUsing?.usernameLower,
            APP_LINKS.PROFILE_EDIT
          )}
          iconLeft={
            <EditIconPen
              style={{ width: 16, height: 16 }}
              className="text-tertirary"
            />
          }
          fullWidth={isMobile}
        >
          Edit Profile
        </ButtonComponent>
      )}
      <RequestConnectionButton
        profile={profileUsing}
        buttonClassName="full-w"
        useDismiss={useDismiss}
      />
    </div>
  )
}
